import { useEffect, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import type { NikolaAction, NikolaActivity } from "@chessla/shared";
import { NIKOLA_FAILURE_REPLY } from "@/features/chat/messaging/chat-contracts";
import type { AnalyzedMoveResult } from "@/shared/api/engine.api";
import { ApiRequestError } from "@/shared/api/client";
import { useAuth } from "@/app/context/AuthContext";
import { SingleGameAnalysisModal } from "@/features/analysis";
import {
  ChatComposer,
  NikolaChatSurface,
  NikolaComingSoon,
  ChatTurn,
  currentNikolaActivityLabel,
  NikolaActivityStatus,
  useChatViewport,
} from "@/features/chat/ui";
import type { BoardSegment } from "@/features/chat";
import { withAtomicPositionProof } from "@/features/chat/evaluation";
import { browserEngine, browserEngineActivity, storeBrowserEngineArtifact } from "@/features/analysis/browser-engine";
import {
  confirmPracticeSet,
  createCoachingSession,
  downloadCoachExport,
  fetchChatCreditBalance,
  fetchChatCreditEstimate,
  fetchSessionMessages,
  type CreditBalanceResult,
  type PendingAction,
  type SendMessageResult,
} from "@/features/chat/messaging/chat.api";
import {
  acquireNikolaBrowserActionLease,
  nikolaBrowserResultInput,
  type NikolaBrowserResultInput,
  type NikolaBrowserLeaseHandle,
} from "@/features/chat/messaging/nikola-run.api";
import { useNikolaRun } from "@/features/chat/messaging/use-nikola-run";
import { parseCurrentSessionMessages } from "@/features/chat/messaging/chat-session-messages";
import { mergeNikolaActivityParts, NIKOLA_ANALYSIS_PLACEHOLDER } from "@/features/chat/messaging/chat-message-parts";
import type { ChatStoreMessage } from "@/features/chat/messaging/chat.store";
import { gameReviewUrl, nikolaSettingsUrl } from "@/features/chat/messaging/chat-handoff";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  analysisActivityForProgress,
  gameNikolaResponseMessages,
  replaceGameNikolaWorkingMessage,
  isEmbeddedBrowserAction,
  reviewQuestionScope,
  reviewQuestionSubmission,
} from "./game-nikola";
import { useEntitlementsStore } from "@/features/entitlements/entitlements.store";
import { resolveAnalysisAction, resolveNikolaAction } from "@/features/entitlements/entitlement-policy";
import { useEntitlementCta } from "@/features/entitlements/use-entitlement-cta";
import { useNotification } from "@/app/context/NotificationContext";
import { analysisRunner } from "@/features/analysis/analysis-runner";
import { claimAnalysis, fetchAnalysisGame } from "@/features/analysis/analysis.api";
import { useAnalysisStore } from "@/features/analysis/analysis.store";
import { resolveNikolaAnalysisPreset } from "@/features/analysis/analysis-presets";
import { latestNikolaBoardSegment } from "./nikola-board-command";
import { GameChatContextControl } from "./GameChatContextControl";

interface GameNikolaPanelProps {
  gameId: string;
  currentMove: AnalyzedMoveResult | null;
  onBoardCommand?: (segment: BoardSegment) => void;
}

const NO_ANSWER_MESSAGE = NIKOLA_FAILURE_REPLY;

const workingMessage = (activity: NikolaActivity): ChatStoreMessage => ({
  role: "assistant",
  content: NIKOLA_ANALYSIS_PLACEHOLDER,
  parts: [{ type: "activity", activity }],
});

export const embeddedBrowserEngineResult = (
  action: PendingAction,
  result: { leaseToken: string; artifactId?: string; gameId?: string; failed?: boolean },
): NikolaBrowserResultInput => {
  return nikolaBrowserResultInput(action, result);
};

export const GameNikolaPanel = (props: GameNikolaPanelProps) => {
  const { userId } = useAuth();
  if (import.meta.env.VITE_NIKOLA_DISABLED === "true") return <NikolaComingSoon />;
  return <GameNikolaPanelContent key={userId ?? "signed-out"} {...props} userId={userId} />;
};

const GameNikolaPanelContent = ({
  gameId,
  currentMove,
  onBoardCommand,
  userId,
}: GameNikolaPanelProps & { userId: string | null }) => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatStoreMessage[]>([]);
  const latestMessage = messages.at(-1);
  const { transcriptProps, composerRef, resumeFollow } = useChatViewport(
    messages,
    latestMessage?.role === "assistant" &&
      latestMessage.content !== NIKOLA_ANALYSIS_PLACEHOLDER &&
      Boolean(latestMessage.content.trim()),
  );
  const activityStatusLabel = currentNikolaActivityLabel(messages);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysisModalGameId, setAnalysisModalGameId] = useState<string | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [balance, setBalance] = useState<CreditBalanceResult | null>(null);
  const [estimatedCredits, setEstimatedCredits] = useState<number | null>(null);
  const [creditBlocked, setCreditBlocked] = useState(false);
  const [pinnedMove, setPinnedMove] = useState<AnalyzedMoveResult | null>(null);
  const entitlementSnapshot = useEntitlementsStore((state) => state.snapshot);
  const browserAnalysisProgress = useAnalysisStore((state) => state.progressByGameId[gameId] ?? null);
  const entitlementLoadError = useEntitlementsStore((state) => state.loadError);
  const refreshEntitlements = useEntitlementsStore((state) => state.refresh);
  const nikolaGate = resolveNikolaAction(entitlementSnapshot, "chat", gameId);
  const entitlementCta = useEntitlementCta(nikolaGate);
  const loadRevision = useRef(0);
  const turnRevision = useRef(0);
  const lastQuestionRef = useRef<string | null>(null);
  const terminalRunRef = useRef<object | null>(null);
  const actionLeaseRef = useRef<NikolaBrowserLeaseHandle | null>(null);
  const {
    state: nikolaRunState,
    result: latestNikolaRunResult,
    error: nikolaRunError,
    execute: executeNikolaRun,
    submit: submitNikolaRunResult,
    recover: recoverNikolaRun,
    cancel: cancelActiveNikolaRun,
    fail: failNikolaRun,
    signal: nikolaRunSignal,
  } = useNikolaRun(userId, sessionId);
  const runBusy = nikolaRunState.phase === "running" || nikolaRunState.phase === "waiting";
  const sendingRef = useRef(false);
  const appliedBoardKeyRef = useRef("");
  const continuedActionsRef = useRef(new Set<string>());

  useEffect(() => {
    const revision = ++loadRevision.current;
    ++turnRevision.current;
    sendingRef.current = false;
    setSending(false);
    terminalRunRef.current = null;
    appliedBoardKeyRef.current = "";
    continuedActionsRef.current.clear();
    setSessionId(null);
    setMessages([]);
    setPinnedMove(null);
    setLoading(true);
    setError(null);
    setAnalysisModalGameId(null);
    void Promise.all([
      createCoachingSession("Game Review", gameId),
      fetchChatCreditBalance().catch(() => null),
      fetchChatCreditEstimate().catch(() => null),
    ])
      .then(async ([session, nextBalance, estimate]) => {
        const history = await fetchSessionMessages(session.sessionId);
        if (loadRevision.current !== revision) return;
        setSessionId(session.sessionId);
        const restored = parseCurrentSessionMessages(history.messages);
        setMessages(restored);
        lastQuestionRef.current =
          restored
            .slice()
            .reverse()
            .find(({ role }) => role === "user")?.content ?? null;
        setBalance(nextBalance);
        setEstimatedCredits(estimate?.reserved ?? null);
      })
      .catch((caught: unknown) => {
        if (loadRevision.current !== revision) return;
        const message =
          caught instanceof ApiRequestError ? caught.message : "Nikola's game thread could not be opened. Try again.";
        setError(message);
        if (caught instanceof ApiRequestError) showNotification(message, "error");
      })
      .finally(() => {
        if (loadRevision.current === revision) setLoading(false);
      });
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps -- Cleanup must invalidate only the request that is still current.
      if (loadRevision.current === revision) ++loadRevision.current;
      // The durable run continues outside this surface; only stale UI callbacks are invalidated.
      // eslint-disable-next-line react-hooks/exhaustive-deps -- Cleanup invalidates callbacks from the game being left.
      ++turnRevision.current;
      actionLeaseRef.current?.stop();
      actionLeaseRef.current = null;
    };
  }, [gameId, loadAttempt, showNotification, userId]);

  useEffect(() => {
    if (!browserAnalysisProgress) return;
    const activity = analysisActivityForProgress(browserAnalysisProgress);
    setMessages((current) =>
      current.map((message) => ({
        ...message,
        parts: message.parts?.map((part) =>
          part.type === "activity" && part.activity.kind === "analysis_started" && part.activity.status === "running"
            ? { ...part, activity: { ...part.activity, ...activity } }
            : part,
        ),
      })),
    );
  }, [browserAnalysisProgress]);

  useEffect(() => {
    const segment = latestNikolaBoardSegment(messages);
    if (!segment) return;
    const key = JSON.stringify(segment);
    if (appliedBoardKeyRef.current === key) return;
    appliedBoardKeyRef.current = key;
    onBoardCommand?.(segment);
  }, [messages, onBoardCommand]);

  const updateWorkingActivity = (activity: NikolaActivity): void =>
    setMessages((current) => {
      const working = current.at(-1);
      if (working?.role !== "assistant") return current;
      return [...current.slice(0, -1), { ...working, parts: mergeNikolaActivityParts(working.parts, activity) }];
    });
  const replaceWorkingWith = (messages: readonly ChatStoreMessage[]): void =>
    setMessages((current) => replaceGameNikolaWorkingMessage(current, messages));

  const refreshBalance = (): void => {
    void fetchChatCreditBalance()
      .then(setBalance)
      .catch(() => undefined);
  };

  const executeBrowserAction = async (
    action: PendingAction,
    revision: number,
    signal: AbortSignal,
  ): Promise<SendMessageResult | null> => {
    if (!sessionId) return null;
    const lease = await acquireNikolaBrowserActionLease(action, signal);
    if (turnRevision.current !== revision) {
      lease?.stop();
      return null;
    }
    if (!lease) return recoverNikolaRun(sessionId, () => undefined);
    actionLeaseRef.current = lease;
    try {
      lease.signal.throwIfAborted();
      let input: NikolaBrowserResultInput;
      if (action.type === "browser_engine" && action.request) {
        try {
          const calculated = await browserEngine.calculate(
            action.request,
            ({ depth, nodes, targetDepth }) => {
              if (turnRevision.current !== revision) return;
              updateWorkingActivity(browserEngineActivity(action.request!, { depth, nodes, targetDepth }));
            },
            lease.signal,
          );
          const artifact = withAtomicPositionProof(calculated);
          if (turnRevision.current !== revision) return null;
          const stored = await storeBrowserEngineArtifact(artifact);
          input = embeddedBrowserEngineResult(action, { leaseToken: lease.leaseToken, artifactId: stored.artifactId });
        } catch {
          input = embeddedBrowserEngineResult(action, { leaseToken: lease.leaseToken, failed: true });
        }
      } else if (action.type === "start_game_analysis" && action.gameId === gameId) {
        const game = await fetchAnalysisGame(gameId);
        if (turnRevision.current !== revision) return null;
        lease.signal.throwIfAborted();
        const preset = resolveNikolaAnalysisPreset(action.analysisPreset ?? "regular");
        if (
          action.analysisMode !== "reanalyze" &&
          preset &&
          game.tacticalStatus === "verified" &&
          game.existingAnalysisDepth != null &&
          game.existingAnalysisDepth >= preset.depth
        ) {
          return submitNikolaRunResult(
            embeddedBrowserEngineResult(action, { leaseToken: lease.leaseToken, gameId }),
            (activity) => {
              if (turnRevision.current === revision) updateWorkingActivity(activity);
            },
          );
        }
        const gate = resolveAnalysisAction(
          entitlementSnapshot,
          gameId,
          game.existingAnalysisDepth != null,
          game.exactTimeControl,
        );
        if (!gate.allowed) {
          if (gate.cta) void navigate(gate.cta === "manage_trial" ? "/subscription#trial" : "/subscription");
          throw new Error("Nikola cannot start this game's analysis with the current access.");
        }
        await claimAnalysis(gameId);
        if (turnRevision.current !== revision) return null;
        lease.signal.throwIfAborted();
        const completion = preset ? analysisRunner.tryStart([game], preset, "single") : null;
        const cancel = () => analysisRunner.stop();
        lease.signal.addEventListener("abort", cancel, { once: true });
        if (completion) await completion.finally(() => lease.signal.removeEventListener("abort", cancel));
        const job = useAnalysisStore.getState().jobs.find((candidate) => candidate.gameId === gameId);
        input = {
          ...embeddedBrowserEngineResult(action, {
            leaseToken: lease.leaseToken,
            gameId,
            failed: !completion || job?.status !== "done",
          }),
        };
      } else {
        throw new Error("Unsupported game action");
      }
      if (turnRevision.current !== revision) return null;
      lease.signal.throwIfAborted();
      lease.stop();
      return submitNikolaRunResult(input, (activity) => {
        if (turnRevision.current === revision) updateWorkingActivity(activity);
      });
    } finally {
      lease.stop();
      if (actionLeaseRef.current === lease) actionLeaseRef.current = null;
    }
  };

  useEffect(() => {
    if (!sessionId || loading || sendingRef.current || nikolaRunState.phase === "running") return;
    const continueResult = async (result: SendMessageResult): Promise<void> => {
      const action = result.pendingAction;
      if (!action) return;
      if (!isEmbeddedBrowserAction(action, gameId)) {
        failNikolaRun(new Error("Nikola requested an action outside this game."));
        return;
      }
      const key =
        action.actionId ?? action.idempotencyKey ?? `${action.type}:${action.gameId ?? action.request?.requestId}`;
      if (continuedActionsRef.current.has(key)) return;
      continuedActionsRef.current.add(key);
      const revision = ++turnRevision.current;
      sendingRef.current = true;
      setSending(true);
      setMessages((current) => [
        ...current,
        workingMessage({
          kind: action.type === "start_game_analysis" ? "analysis_started" : "line_compared",
          status: "running",
          label: action.type === "start_game_analysis" ? "READING THIS GAME" : "VERIFYING THIS POSITION",
        }),
      ]);
      try {
        let completed = await executeBrowserAction(action, revision, nikolaRunSignal());
        while (turnRevision.current === revision && completed?.pendingAction) {
          const next = completed.pendingAction;
          if (!isEmbeddedBrowserAction(next, gameId)) throw new Error("Nikola requested an action outside this game.");
          const nextKey =
            next.actionId ?? next.idempotencyKey ?? `${next.type}:${next.gameId ?? next.request?.requestId}`;
          if (continuedActionsRef.current.has(nextKey)) throw new Error("Nikola repeated a completed browser action.");
          continuedActionsRef.current.add(nextKey);
          completed = await executeBrowserAction(next, revision, nikolaRunSignal());
        }
        if (turnRevision.current !== revision) return;
        if (!completed) {
          replaceWorkingWith([]);
          failNikolaRun(new Error(NO_ANSWER_MESSAGE));
          return;
        }
        terminalRunRef.current = completed;
        if (completed.status === "failed") {
          replaceWorkingWith([]);
          if (completed.failureCode !== "cancelled") {
            setError(completed.failureMessage ?? NO_ANSWER_MESSAGE);
            if (completed.retryable && lastQuestionRef.current) setDraft(lastQuestionRef.current);
            refreshBalance();
          }
          return;
        }
        if (!completed.pendingAction) replaceWorkingWith(gameNikolaResponseMessages(completed));
      } catch (caught) {
        if (turnRevision.current !== revision) return;
        replaceWorkingWith([
          {
            role: "assistant",
            content: caught instanceof ApiRequestError ? caught.message : NO_ANSWER_MESSAGE,
            ...(lastQuestionRef.current ? { failedDraft: lastQuestionRef.current } : {}),
          },
        ]);
        failNikolaRun(caught);
      } finally {
        if (turnRevision.current === revision) {
          sendingRef.current = false;
          setSending(false);
        }
      }
    };
    if (nikolaRunState.phase === "waiting" && latestNikolaRunResult) {
      void continueResult(latestNikolaRunResult);
      return;
    }
    if (nikolaRunState.phase === "idle") {
      void recoverNikolaRun(sessionId, () => undefined).catch(() => undefined);
      return;
    }
    if (
      terminalRunRef.current === nikolaRunState ||
      (latestNikolaRunResult && terminalRunRef.current === latestNikolaRunResult)
    )
      return;
    terminalRunRef.current = nikolaRunState;
    setSending(false);
    if (nikolaRunState.phase === "failed") {
      replaceWorkingWith([]);
      setError(
        latestNikolaRunResult?.failureMessage ??
          (nikolaRunError instanceof ApiRequestError ? nikolaRunError.message : NO_ANSWER_MESSAGE),
      );
      if (latestNikolaRunResult?.retryable && lastQuestionRef.current) setDraft(lastQuestionRef.current);
      return;
    }
    if (nikolaRunState.phase !== "completed") return;
    const revision = turnRevision.current;
    void fetchSessionMessages(sessionId)
      .then((history) => {
        if (turnRevision.current === revision && terminalRunRef.current !== latestNikolaRunResult)
          setMessages(parseCurrentSessionMessages(history.messages));
      })
      .catch((caught: unknown) => {
        if (turnRevision.current === revision)
          setError(caught instanceof ApiRequestError ? caught.message : NO_ANSWER_MESSAGE);
      });
    // Shared executor persists outside this panel; action identity prevents duplicate continuation.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Execution helpers bind current game state and are guarded by revisions.
  }, [gameId, latestNikolaRunResult, loading, nikolaRunError, nikolaRunState, recoverNikolaRun, sessionId]);

  const submit = async (message: string, retryDraft: string): Promise<void> => {
    const text = message.trim();
    if (!text) return;
    if (!nikolaGate.allowed) {
      if (entitlementCta) void navigate(entitlementCta.href);
      return;
    }
    if (!sessionId || sendingRef.current) return;
    lastQuestionRef.current = text;
    const revision = ++turnRevision.current;
    sendingRef.current = true;
    resumeFollow();
    setSending(true);
    setError(null);
    setCreditBlocked(false);
    setMessages((current) => [
      ...current,
      { role: "user", content: text },
      workingMessage({
        kind: "coach_thinking",
        status: "running",
        label: "UNDERSTANDING YOUR QUESTION",
        detail: "Choosing the verified evidence needed for this answer.",
      }),
    ]);
    setDraft("");
    try {
      let result = await executeNikolaRun(
        {
          sessionId,
          message: text,
          requestId: crypto.randomUUID(),
          ...reviewQuestionScope(gameId, pinnedMove),
        },
        (activity) => {
          if (turnRevision.current === revision) updateWorkingActivity(activity);
        },
      );
      if (turnRevision.current !== revision) return;
      while (result.pendingAction && isEmbeddedBrowserAction(result.pendingAction, gameId)) {
        const action = result.pendingAction;
        continuedActionsRef.current.add(
          action.actionId ?? action.idempotencyKey ?? `${action.type}:${action.gameId ?? action.request?.requestId}`,
        );
        replaceWorkingWith([
          ...(result.reply
            ? gameNikolaResponseMessages({ ...result, pendingAction: null, creditsCharged: undefined })
            : []),
          workingMessage({
            kind: action.type === "start_game_analysis" ? "analysis_started" : "line_compared",
            status: "running",
            label: action.type === "start_game_analysis" ? "READING THIS GAME" : "VERIFYING THIS POSITION",
          }),
        ]);
        const completed = await executeBrowserAction(action, revision, nikolaRunSignal());
        if (completed) result = completed;
        else throw new Error(NO_ANSWER_MESSAGE);
      }
      if (turnRevision.current !== revision) return;
      if (result.pendingAction) throw new Error("Nikola requested an action outside this game.");
      terminalRunRef.current = result;
      if (result.status === "failed") {
        replaceWorkingWith([]);
        setError(result.failureMessage ?? NO_ANSWER_MESSAGE);
        if (result.retryable) setDraft(retryDraft);
        refreshBalance();
        return;
      }
      replaceWorkingWith(gameNikolaResponseMessages(result));
      refreshBalance();
    } catch (caught) {
      if (turnRevision.current !== revision) return;
      const message = caught instanceof ApiRequestError ? caught.message : NO_ANSWER_MESSAGE;
      replaceWorkingWith([]);
      setError(message);
      failNikolaRun(caught);
      if (caught instanceof ApiRequestError && caught.status === 402) setCreditBlocked(true);
      setDraft(retryDraft);
    } finally {
      if (turnRevision.current === revision) {
        sendingRef.current = false;
        setSending(false);
      }
    }
  };

  const handleResponseAction = (action: NikolaAction): void => {
    if (action.type === "start_game_analysis" && action.gameId) {
      setAnalysisModalGameId(action.gameId);
      return;
    }
    if (action.type === "evaluate_line" && action.fen) {
      const message = `Evaluate this position: ${action.fen}`;
      void submit(message, message);
      return;
    }
    if (action.type === "save_practice_set" && action.draftId) {
      void confirmPracticeSet(action.draftId)
        .then((result) => showNotification(`${result.title} saved · ${result.positionCount} positions`))
        .catch((caught: unknown) =>
          showNotification(
            caught instanceof ApiRequestError
              ? caught.message
              : "That practice set could not be saved. Please try again.",
            "error",
          ),
        );
      return;
    }
    if (action.type === "download_export" && action.exportId) {
      void downloadCoachExport(action.exportId).catch((caught: unknown) =>
        showNotification(
          caught instanceof ApiRequestError ? caught.message : "That download could not be prepared. Please try again.",
          "error",
        ),
      );
      return;
    }
    if (action.type === "open_game" && action.gameId) {
      void navigate(gameReviewUrl({ gameId: action.gameId }));
      return;
    }
    if (action.type === "open_nikola_settings") {
      void navigate(nikolaSettingsUrl);
      return;
    }
    if (action.type === "open_pattern" && action.patternId) {
      void navigate(`/patterns?patternId=${encodeURIComponent(action.patternId)}`);
      return;
    }
  };

  return (
    <Box
      component="section"
      data-testid="game-nikola-panel"
      data-fill-workspace="true"
      aria-labelledby="game-nikola-title"
      sx={{
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        minHeight: 0,
        height: { xs: "100dvh", lg: "100%" },
        maxHeight: "100dvh",
        overflow: "hidden",
        borderBlock: "1px solid",
        borderColor: "divider",
        bgcolor: "background.default",
      }}
    >
      <SingleGameAnalysisModal
        gameId={analysisModalGameId}
        onClose={() => setAnalysisModalGameId(null)}
        onComplete={({ gameId: completedGameId }) => {
          setAnalysisModalGameId(null);
          void navigate(gameReviewUrl({ gameId: completedGameId }));
        }}
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 1.5,
          py: 1.25,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box>
          <Typography
            id="game-nikola-title"
            sx={{
              display: "flex",
              alignItems: "baseline",
              gap: 0.45,
              fontSize: ".82rem",
              fontWeight: 950,
              letterSpacing: ".08em",
            }}
          >
            ASK NIKOLA{" "}
            <Box component="span" aria-hidden sx={{ fontSize: "1.15em", lineHeight: 0.7 }}>
              ♞
            </Box>
          </Typography>
          <Typography sx={{ mt: 0.3, color: "text.secondary", fontSize: ".58rem", letterSpacing: ".07em" }}>
            POSITION · LINE · COMPARE · PRACTICE
          </Typography>
        </Box>
        {sessionId && (
          <Button
            component={RouterLink}
            to={`/chat?sessionId=${encodeURIComponent(sessionId)}`}
            sx={{ minHeight: 44, borderRadius: 0, color: "primary.main", fontSize: ".58rem", fontWeight: 900 }}
          >
            OPEN FULL CHAT ↗
          </Button>
        )}
      </Box>
      {!entitlementSnapshot && entitlementLoadError && (
        <Box role="alert" sx={{ px: 1.5, py: 1.25, borderBottom: "1px solid", borderColor: "divider" }}>
          <Typography sx={{ color: "secondary.main", fontSize: ".68rem", fontWeight: 800 }}>
            Nikola access could not be checked.
          </Typography>
          <Button onClick={() => void refreshEntitlements()} sx={{ mt: 0.5, minHeight: 44, borderRadius: 0 }}>
            Retry access check
          </Button>
        </Box>
      )}
      <NikolaChatSurface
        variant="embedded"
        transcriptProps={transcriptProps}
        composerRef={composerRef}
        threadProps={{
          "data-testid": "game-nikola-thread",
        }}
        threadSx={{ px: { xs: 1, sm: 1.5 }, pt: messages.length ? 1.5 : 0 }}
        afterThread={
          <>
            <NikolaActivityStatus label={activityStatusLabel} />
            {error && (
              <Box role="alert" sx={{ px: 1.5, py: 1, borderTop: "1px solid", borderColor: "divider" }}>
                <Typography sx={{ color: "secondary.main", fontSize: ".68rem", fontWeight: 800 }}>{error}</Typography>
                {!sessionId && !loading && (
                  <Button
                    onClick={() => setLoadAttempt((attempt) => attempt + 1)}
                    sx={{ mt: 0.5, minHeight: 44, borderRadius: 0 }}
                  >
                    Retry opening Nikola
                  </Button>
                )}
              </Box>
            )}
          </>
        }
        composer={
          <ChatComposer
            draft={draft}
            context={null}
            fixedContextLabel={
              <GameChatContextControl
                currentMove={currentMove}
                pinnedMove={pinnedMove}
                disabled={loading || sending || runBusy}
                onPin={setPinnedMove}
                onClear={() => setPinnedMove(null)}
              />
            }
            embedded
            disabled={loading || sending || runBusy || !sessionId || !nikolaGate.allowed}
            running={sending || runBusy}
            balance={balance}
            estimatedCredits={estimatedCredits ?? nikolaGate.estimate}
            blocked={creditBlocked || !nikolaGate.allowed}
            blockedLabel={entitlementCta?.label}
            blockedHref={entitlementCta?.href}
            blockedDetail={entitlementCta?.detail}
            onChange={setDraft}
            onContextChange={() => undefined}
            onSend={() => {
              const submission = reviewQuestionSubmission(draft);
              void submit(submission.request, submission.retryDraft);
            }}
            onCancel={() => {
              cancelActiveNikolaRun();
              ++turnRevision.current;
              sendingRef.current = false;
              setSending(false);
              setMessages((current) => current.filter((message) => message.content !== NIKOLA_ANALYSIS_PLACEHOLDER));
            }}
          />
        }
      >
        <Box sx={{ minHeight: "100%" }}>
          {messages.map((message, index) => (
            <ChatTurn
              key={`${message.createdAt ?? "live"}:${index}`}
              message={message}
              disabled={sending || runBusy || !nikolaGate.allowed}
              estimatedCredits={estimatedCredits ?? nikolaGate.estimate}
              placeholder={message.content === NIKOLA_ANALYSIS_PLACEHOLDER}
              embedded
              onAction={handleResponseAction}
              onRetry={message.failedDraft ? () => void submit(message.failedDraft!, message.failedDraft!) : undefined}
            />
          ))}
        </Box>
      </NikolaChatSurface>
    </Box>
  );
};
